import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { keyframes } from "@mui/material/styles";

// Skeleton animations (MUI default patterns)
const pulseKeyframe = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

const waveKeyframe = keyframes`
  0% {
    transform: translateX(-100%);
  }
  50% {
    /* +0.5s of delay between each loop */
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export type SkeletonVariant =
	| "rectangular"
	| "circular"
	| "rounded"
	| "text"
	| "chip"
	| "thumbnail"
	| "avatar";
export type SkeletonAnimation = "wave" | "pulse" | false;

export interface SkeletonProps {
	width?: string | number;
	height?: string | number;
	variant?: SkeletonVariant;
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
	[key: string]: unknown;
}

/**
 * Basic skeleton component for loading states
 */
export function Skeleton({
	width = "100%",
	height = 20,
	variant = "rectangular",
	animation = "wave",
	sx = {},
	...props
}: SkeletonProps): React.JSX.Element {
	// Smart defaults based on variant
	const getDefaultDimensions = () => {
		switch (variant) {
			case "text": {
				return { width: "100%", height: 20 };
			}
			case "chip": {
				return { width: 80, height: 24 };
			}
			case "thumbnail": {
				return { width: "100%", height: "auto", aspectRatio: "16/9" };
			}
			case "avatar": {
				return { width: 40, height: 40 };
			}
			case "circular": {
				return { width: height, height: height };
			}
			default: {
				return { width, height };
			}
		}
	};

	const defaultDimensions = getDefaultDimensions();
	const finalWidth = width === "100%" ? defaultDimensions.width : width;
	const finalHeight = height === 20 ? defaultDimensions.height : height;

	const getBorderRadius = () => {
		switch (variant) {
			case "circular":
			case "avatar": {
				return "50%";
			}
			case "rounded":
			case "chip": {
				return 1;
			}
			case "thumbnail": {
				return 1;
			}
			case "text": {
				return 0.5;
			}
			default: {
				return 0;
			}
		}
	};

	const baseStyles = {
		backgroundColor: "var(--mui-palette-action-hover)",
		borderRadius: getBorderRadius(),
		display: "inline-block",
		width: finalWidth,
		height: finalHeight,
		...(variant === "thumbnail" &&
			defaultDimensions.aspectRatio && {
				aspectRatio: defaultDimensions.aspectRatio,
			}),
		...(animation === "wave" && {
			overflow: "hidden",
			position: "relative",
			"&::after": {
				animation: `${waveKeyframe} 2s linear 0.5s infinite`,
				background: `linear-gradient(
					90deg,
					transparent,
					var(--mui-palette-action-hover),
					transparent
				)`,
				content: '""',
				position: "absolute",
				transform: "translateX(-100%)",
				inset: 0,
			},
		}),
		...(animation === "pulse" && {
			animation: `${pulseKeyframe} 2s ease-in-out 0.5s infinite`,
		}),
		...sx,
	};

	return (
		<Box
			sx={baseStyles}
			{...props}
		/>
	);
}

// Layout Components
export interface SkeletonGroupProps {
	children: React.ReactNode;
	spacing?: number;
	sx?: Record<string, unknown>;
}

/**
 * Container for grouping skeleton components with consistent spacing
 */
export function SkeletonGroup({
	children,
	spacing = 1,
	sx = {},
}: SkeletonGroupProps): React.JSX.Element {
	return <Box sx={{ "& > *": { mb: spacing }, ...sx }}>{children}</Box>;
}

export interface SkeletonRowProps {
	children: React.ReactNode;
	spacing?: number;
	alignItems?: string;
	sx?: Record<string, unknown>;
}

/**
 * Horizontal layout for skeleton components
 */
export function SkeletonRow({
	children,
	spacing = 1,
	alignItems = "center",
	sx = {},
}: SkeletonRowProps): React.JSX.Element {
	return (
		<Stack
			direction="row"
			spacing={spacing}
			sx={{ alignItems, ...sx }}>
			{children}
		</Stack>
	);
}

export interface SkeletonStackProps {
	children: React.ReactNode;
	spacing?: number;
	sx?: Record<string, unknown>;
}

/**
 * Vertical layout for skeleton components
 */
export function SkeletonStack({
	children,
	spacing = 2,
	sx = {},
}: SkeletonStackProps): React.JSX.Element {
	return (
		<Stack
			spacing={spacing}
			sx={sx}>
			{children}
		</Stack>
	);
}

// Text Skeleton Components
export interface TextSkeletonProps {
	lines?: number;
	variant?:
		| "h1"
		| "h2"
		| "h3"
		| "h4"
		| "h5"
		| "h6"
		| "body1"
		| "body2"
		| "caption";
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
}

/**
 * Multi-line text skeleton with realistic line width variation
 */
export function TextSkeleton({
	lines = 2,
	variant = "body1",
	animation = "wave",
	sx = {},
}: TextSkeletonProps): React.JSX.Element {
	const getHeightForVariant = () => {
		switch (variant) {
			case "h1": {
				return 48;
			}
			case "h2": {
				return 36;
			}
			case "h3": {
				return 32;
			}
			case "h4": {
				return 28;
			}
			case "h5": {
				return 24;
			}
			case "h6": {
				return 20;
			}
			case "body1": {
				return 20;
			}
			case "body2": {
				return 18;
			}
			case "caption": {
				return 16;
			}
			default: {
				return 20;
			}
		}
	};

	const height = getHeightForVariant();
	const lineWidths = ["100%", "85%", "75%", "95%", "60%"];

	return (
		<SkeletonStack
			spacing={0.5}
			sx={sx}>
			{Array.from({ length: lines }, (_, index) => (
				<Skeleton
					key={index}
					variant="text"
					width={lineWidths[index % lineWidths.length]}
					height={height}
					animation={animation}
				/>
			))}
		</SkeletonStack>
	);
}

export interface ChipSkeletonProps {
	count?: number;
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
}

/**
 * Multiple chip skeletons in a row
 */
export function ChipSkeleton({
	count = 3,
	animation = "wave",
	sx = {},
}: ChipSkeletonProps): React.JSX.Element {
	const chipWidths = [60, 80, 70, 90, 50];

	return (
		<SkeletonRow
			spacing={1}
			sx={sx}>
			{Array.from({ length: count }, (_, index) => (
				<Skeleton
					key={index}
					variant="chip"
					width={chipWidths[index % chipWidths.length]}
					animation={animation}
				/>
			))}
		</SkeletonRow>
	);
}

// Card Skeleton Components
export interface CardSkeletonProps {
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
}

/**
 * Generic card skeleton with thumbnail and content
 */
export function CardSkeleton({
	animation = "wave",
	sx = {},
}: CardSkeletonProps): React.JSX.Element {
	return (
		<Card sx={{ height: "100%", ...sx }}>
			<Skeleton
				variant="thumbnail"
				animation={animation}
			/>
			<CardContent sx={{ p: 3 }}>
				<SkeletonStack spacing={2}>
					<TextSkeleton
						lines={2}
						variant="h6"
						animation={animation}
					/>
					<TextSkeleton
						lines={2}
						variant="body2"
						animation={animation}
					/>
					<SkeletonRow spacing={1}>
						<Skeleton
							variant="avatar"
							width={20}
							height={20}
							animation={animation}
						/>
						<Skeleton
							variant="text"
							width={100}
							height={16}
							animation={animation}
						/>
						<Skeleton
							variant="circular"
							width={20}
							height={20}
							animation={animation}
						/>
					</SkeletonRow>
				</SkeletonStack>
			</CardContent>
		</Card>
	);
}

/**
 * MapCard skeleton with realistic layout matching MapCard component
 */
export function MapCardSkeleton({
	animation = "wave",
	sx = {},
}: CardSkeletonProps): React.JSX.Element {
	return (
		<Card
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				borderRadius: 2,
				...sx,
			}}>
			{/* Thumbnail */}
			<Box
				sx={{
					pt: "calc(100% * 9 / 16)",
					position: "relative",
					borderRadius: "8px 8px 0 0",
					overflow: "hidden",
				}}>
				<Box
					sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
					<Skeleton
						variant="thumbnail"
						animation={animation}
						sx={{ height: "100%", borderRadius: 0 }}
					/>
				</Box>
			</Box>

			{/* Content */}
			<CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
				<SkeletonStack spacing={2}>
					{/* Title with actions */}
					<SkeletonRow
						spacing={1}
						alignItems="flex-start">
						<TextSkeleton
							lines={2}
							variant="h6"
							animation={animation}
							sx={{ flex: 1 }}
						/>
						<Skeleton
							variant="circular"
							width={24}
							height={24}
							animation={animation}
						/>
					</SkeletonRow>

					{/* Description */}
					<TextSkeleton
						lines={2}
						variant="body2"
						animation={animation}
					/>

					{/* Metadata */}
					<SkeletonRow
						spacing={1.5}
						sx={{ pt: 0.5 }}>
						<Skeleton
							variant="text"
							width={80}
							height={16}
							animation={animation}
						/>
						<Skeleton
							variant="circular"
							width={4}
							height={4}
							animation={animation}
						/>
						<Skeleton
							variant="chip"
							width={60}
							height={20}
							animation={animation}
						/>
						<Skeleton
							variant="circular"
							width={4}
							height={4}
							animation={animation}
						/>
						<SkeletonRow spacing={0.5}>
							<Skeleton
								variant="circular"
								width={12}
								height={12}
								animation={animation}
							/>
							<Skeleton
								variant="text"
								width={100}
								height={16}
								animation={animation}
							/>
						</SkeletonRow>
					</SkeletonRow>
				</SkeletonStack>
			</CardContent>
		</Card>
	);
}

/**
 * DatasetCard skeleton with geometry icon and chips
 */
export function DatasetCardSkeleton({
	animation = "wave",
	sx = {},
}: CardSkeletonProps): React.JSX.Element {
	return (
		<Card
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				borderRadius: 2,
				...sx,
			}}>
			{/* Thumbnail */}
			<Box
				sx={{
					pt: "calc(100% * 9 / 16)",
					position: "relative",
					borderRadius: "8px 8px 0 0",
					overflow: "hidden",
				}}>
				<Box
					sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
					<Skeleton
						variant="thumbnail"
						animation={animation}
						sx={{ height: "100%", borderRadius: 0 }}
					/>
				</Box>
			</Box>

			{/* Content */}
			<CardContent sx={{ p: 3, flex: 1 }}>
				<SkeletonStack spacing={2}>
					{/* Title with geometry icon and actions */}
					<SkeletonRow
						spacing={1}
						alignItems="flex-start">
						<Skeleton
							variant="circular"
							width={20}
							height={20}
							animation={animation}
						/>
						<TextSkeleton
							lines={2}
							variant="h6"
							animation={animation}
							sx={{ flex: 1 }}
						/>
						<SkeletonRow spacing={0.5}>
							<Skeleton
								variant="chip"
								width={60}
								height={20}
								animation={animation}
							/>
							<Skeleton
								variant="circular"
								width={24}
								height={24}
								animation={animation}
							/>
						</SkeletonRow>
					</SkeletonRow>

					{/* Description */}
					<TextSkeleton
						lines={2}
						variant="body2"
						animation={animation}
					/>

					{/* Chips row */}
					<ChipSkeleton
						count={4}
						animation={animation}
					/>
				</SkeletonStack>
			</CardContent>
		</Card>
	);
}

// List Item Skeleton Components
export interface ListItemSkeletonProps {
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
}

/**
 * MapList item skeleton for horizontal list layout
 */
export function MapListItemSkeleton({
	animation = "wave",
	sx = {},
}: ListItemSkeletonProps): React.JSX.Element {
	return (
		<Card
			variant="outlined"
			sx={{ cursor: "pointer", transition: "all .2s ease-in-out", ...sx }}>
			<CardContent sx={{ p: 2 }}>
				<SkeletonRow
					spacing={2}
					alignItems="center">
					{/* Icon placeholder */}
					<Skeleton
						variant="circular"
						width={20}
						height={20}
						animation={animation}
					/>

					{/* Content */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<SkeletonStack spacing={0.5}>
							<Skeleton
								variant="text"
								width="75%"
								height={20}
								animation={animation}
							/>
							<Skeleton
								variant="text"
								width="45%"
								height={16}
								animation={animation}
							/>
						</SkeletonStack>
					</Box>

					{/* Metadata */}
					<SkeletonRow
						spacing={1}
						sx={{ display: { xs: "none", sm: "flex" } }}>
						<Skeleton
							variant="text"
							width={60}
							height={16}
							animation={animation}
						/>
						<Skeleton
							variant="chip"
							width={50}
							height={20}
							animation={animation}
						/>
						<Skeleton
							variant="text"
							width={80}
							height={16}
							animation={animation}
						/>
					</SkeletonRow>

					{/* Actions */}
					<Skeleton
						variant="circular"
						width={24}
						height={24}
						animation={animation}
					/>
				</SkeletonRow>
			</CardContent>
		</Card>
	);
}

/**
 * DatasetList item skeleton with geometry icon and chips
 */
export function DatasetListItemSkeleton({
	animation = "wave",
	sx = {},
}: ListItemSkeletonProps): React.JSX.Element {
	return (
		<Card
			variant="outlined"
			sx={{ cursor: "pointer", transition: "all .2s ease-in-out", ...sx }}>
			<CardContent sx={{ p: 2 }}>
				<SkeletonRow
					spacing={2}
					alignItems="center">
					{/* Geometry Icon */}
					<Skeleton
						variant="circular"
						width={20}
						height={20}
						animation={animation}
					/>

					{/* Content */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<SkeletonStack spacing={1}>
							<Skeleton
								variant="text"
								width="65%"
								height={20}
								animation={animation}
							/>
							<ChipSkeleton
								count={3}
								animation={animation}
							/>
						</SkeletonStack>
					</Box>

					{/* Actions */}
					<SkeletonRow spacing={0.5}>
						<Skeleton
							variant="chip"
							width={40}
							height={20}
							animation={animation}
						/>
						<Skeleton
							variant="circular"
							width={24}
							height={24}
							animation={animation}
						/>
					</SkeletonRow>
				</SkeletonRow>
			</CardContent>
		</Card>
	);
}

// Special Component Skeletons
/**
 * OpposingValues skeleton with label-value pairs
 */
export function OpposingValuesSkeleton({
	items = 4,
	animation = "wave",
	sx = {},
}: {
	items?: number;
	animation?: SkeletonAnimation;
	sx?: Record<string, unknown>;
}): React.JSX.Element {
	return (
		<SkeletonStack
			spacing={1}
			sx={sx}>
			{Array.from({ length: items }, (_, index) => (
				<Box
					key={index}
					sx={{
						display: "grid",
						gridTemplateColumns: "150px minmax(0, 1fr)",
						gap: 1,
						p: 1,
						...(index % 2 === 0 && {
							bgcolor: "var(--mui-palette-background-level1)",
						}),
					}}>
					<Skeleton
						variant="text"
						width="80%"
						height={16}
						animation={animation}
					/>
					<Skeleton
						variant="text"
						width="60%"
						height={16}
						animation={animation}
					/>
				</Box>
			))}
		</SkeletonStack>
	);
}

/**
 * DraggableQueryCard skeleton with drag handle and features
 */
export function DraggableQueryCardSkeleton({
	animation = "wave",
	sx = {},
}: CardSkeletonProps): React.JSX.Element {
	return (
		<Card
			variant="outlined"
			sx={{ transition: "all .2s ease-in-out", ...sx }}>
			<CardContent sx={{ p: 2 }}>
				<SkeletonRow
					spacing={2}
					alignItems="flex-start">
					{/* Drag Handle */}
					<Skeleton
						variant="circular"
						width={20}
						height={20}
						animation={animation}
					/>

					{/* Content */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<SkeletonStack spacing={1}>
							{/* Layer name + dataset */}
							<SkeletonStack spacing={0.5}>
								<Skeleton
									variant="text"
									width="70%"
									height={18}
									animation={animation}
								/>
								<Skeleton
									variant="text"
									width="50%"
									height={14}
									animation={animation}
								/>
							</SkeletonStack>

							{/* Feature chips */}
							<ChipSkeleton
								count={3}
								animation={animation}
							/>
						</SkeletonStack>
					</Box>

					{/* Actions */}
					<SkeletonRow spacing={0.5}>
						<Skeleton
							variant="circular"
							width={24}
							height={24}
							animation={animation}
						/>
						<Skeleton
							variant="circular"
							width={24}
							height={24}
							animation={animation}
						/>
					</SkeletonRow>
				</SkeletonRow>
			</CardContent>
		</Card>
	);
}
